import {
  Component,
  HostListener,
  inject,
  OnInit,
} from '@angular/core';

import { CommonModule } from '@angular/common';

import {
  FormsModule,
  NgForm,
} from '@angular/forms';

import Swal from 'sweetalert2';
import { Contact } from '../../services/contact.service';
import { TestimonialService } from '../../services/testimonial.service';
import { Testimonial } from '../../models/testimonial.interface';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
  ],
  templateUrl: './landing.html',
  styleUrls: ['./landing.scss'],
})
export class Landing implements OnInit {

  private readonly testimonialsPerPage = 6;

  private contactService = inject(Contact);
  private testimonialService = inject(TestimonialService);

  isScrolled = false;
  showTopButton = false;
  currentTestimonialPage = 1;
  isSending = false;

  formData = {
    name: '',
    email: '',
    message: '',
  };

  testimonials: Testimonial[] = [];

  ngOnInit(): void {
    this.getTestimonials();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const link = target.closest('a') as HTMLAnchorElement | null;

    if (!link) {
      return;
    }

    const hash = link.getAttribute('href');

    if (!hash || !hash.startsWith('#') || hash === '#') {
      return;
    }

    event.preventDefault();

    const section = document.querySelector(hash) as HTMLElement | null;

    if (!section) {
      return;
    }

    const navbarHeight = 90;
    const top = section.offsetTop - navbarHeight;

    window.scrollTo({
      top,
      behavior: 'smooth',
    });

    history.pushState(null, '', hash);

    // Close mobile menu after selecting a section
    this.closeMobileMenu();
  }

  closeMobileMenu(): void {
    const mobileMenu = document.getElementById('mobileMenu');

    if (!mobileMenu) {
      return;
    }

    const closeButton = mobileMenu.querySelector(
      '[data-bs-dismiss="offcanvas"]'
    ) as HTMLButtonElement | null;

    if (closeButton) {
      closeButton.click();
    }
  }

  getTestimonials(): void {
    this.testimonialService
      .getTestimonials()
      .subscribe({
        next: (testimonials) => {
          this.testimonials = testimonials;
          this.currentTestimonialPage = 1;
        },

        error: (error) => {
          console.error(error);

          this.testimonials = [
            {
              id: 1,
              name: 'Michael R.',
              location: 'Texas',
              message: 'The process helped me understand how to protect my family and prepare for retirement with more confidence.',
              rating: 5,
            },
            {
              id: 2,
              name: 'Sarah L.',
              location: 'California',
              message: 'Very professional, clear, and respectful of my privacy and time. I felt guided from the first conversation.',
              rating: 5,
            },
            {
              id: 3,
              name: 'Daniel M.',
              location: 'Florida',
              message: 'I wanted to make sure my family would be protected. The guidance was practical and easy to understand.',
              rating: 5,
            },
          ];

          this.currentTestimonialPage = 1;
        },
      });
  }

  get totalTestimonialPages(): number {
    return Math.max(
      1,
      Math.ceil(this.testimonials.length / this.testimonialsPerPage)
    );
  }

  get paginatedTestimonials(): Testimonial[] {
    const startIndex =
      (this.currentTestimonialPage - 1) * this.testimonialsPerPage;

    return this.testimonials.slice(
      startIndex,
      startIndex + this.testimonialsPerPage
    );
  }

  get testimonialPageNumbers(): number[] {
    return Array.from(
      { length: this.totalTestimonialPages },
      (_, index) => index + 1
    );
  }

  goToTestimonialPage(page: number): void {
    if (page < 1 || page > this.totalTestimonialPages) {
      return;
    }

    this.currentTestimonialPage = page;
  }

  goToNextTestimonialPage(): void {
    this.goToTestimonialPage(this.currentTestimonialPage + 1);
  }

  goToPreviousTestimonialPage(): void {
    this.goToTestimonialPage(this.currentTestimonialPage - 1);
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const scrollPosition = window.scrollY;

    this.isScrolled = scrollPosition > 50;
    this.showTopButton = scrollPosition > 300;
  }

  scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  getStars(rating: number): number[] {
    return Array.from({ length: rating }, (_, index) => index + 1);
  }

  sendEmail(form: NgForm): void {
    if (form.invalid) {
      return;
    }

    this.isSending = true;

    this.contactService
      .sendContact(this.formData)
      .subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Message Sent',
            text: 'Your inquiry has been sent successfully.',
            confirmButtonText: 'Continue',
            confirmButtonColor: '#562a73',
          });

          form.resetForm();

          this.formData = {
            name: '',
            email: '',
            message: '',
          };

          this.isSending = false;
        },

        error: (error) => {
          console.error(error);

          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: 'Something went wrong. Please try again.',
            confirmButtonColor: '#562a73',
          });

          this.isSending = false;
        },
      });
  }

}